import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const ContactIcon = ({ type }: { type: string }): React.ReactElement => {
  const icons: Record<string, React.ReactElement> = {
    phone: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
          <linearGradient id="ph1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient>
          <filter id="phg"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <rect x="22" y="12" width="36" height="56" rx="8" fill="none" stroke="url(#ph1)" strokeWidth="2" opacity="0.9" filter="url(#phg)"/>
        <rect x="22" y="12" width="36" height="56" rx="8" fill="#4A9EE8" opacity="0.08"/>
        <line x1="22" y1="24" x2="58" y2="24" stroke="#4A9EE8" strokeWidth="1.2" opacity="0.5"/>
        <line x1="22" y1="56" x2="58" y2="56" stroke="#4A9EE8" strokeWidth="1.2" opacity="0.5"/>
        <circle cx="40" cy="63" r="3" fill="#7DBFF5" opacity="0.8" filter="url(#phg)"/>
        <rect x="33" y="17" width="14" height="3" rx="1.5" fill="#4A9EE8" opacity="0.5"/>
        <path d="M32 36 Q32 28 40 28 Q48 28 48 36 Q48 44 40 44 Q36 44 34 42" fill="none" stroke="#7DBFF5" strokeWidth="2" strokeLinecap="round" opacity="0.8" filter="url(#phg)"/>
        <circle cx="40" cy="36" r="4" fill="#4A9EE8" opacity="0.4"/>
        <circle cx="22" cy="12" r="3" fill="#7DBFF5" opacity="0.8"/>
        <circle cx="58" cy="12" r="3" fill="#4A9EE8" opacity="0.7"/>
        <circle cx="58" cy="68" r="3" fill="#4A9EE8" opacity="0.7"/>
        <circle cx="22" cy="68" r="3" fill="#7DBFF5" opacity="0.8"/>
      </svg>
    ),
    email: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
          <linearGradient id="em1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient>
          <filter id="emg"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <rect x="10" y="22" width="60" height="40" rx="6" fill="none" stroke="url(#em1)" strokeWidth="2" opacity="0.9" filter="url(#emg)"/>
        <rect x="10" y="22" width="60" height="40" rx="6" fill="#4A9EE8" opacity="0.07"/>
        <polyline points="10,22 40,46 70,22" fill="none" stroke="#7DBFF5" strokeWidth="2" opacity="0.8" filter="url(#emg)"/>
        <line x1="10" y1="62" x2="28" y2="44" stroke="#4A9EE8" strokeWidth="1.2" opacity="0.5"/>
        <line x1="70" y1="62" x2="52" y2="44" stroke="#4A9EE8" strokeWidth="1.2" opacity="0.5"/>
        <circle cx="40" cy="46" r="4" fill="#7DBFF5" opacity="0.9" filter="url(#emg)"/>
        <circle cx="10" cy="22" r="3" fill="#7DBFF5" opacity="0.8"/>
        <circle cx="70" cy="22" r="3" fill="#4A9EE8" opacity="0.7"/>
        <circle cx="10" cy="62" r="3" fill="#4A9EE8" opacity="0.7"/>
        <circle cx="70" cy="62" r="3" fill="#7DBFF5" opacity="0.8"/>
      </svg>
    ),
    web: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
          <linearGradient id="wb1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient>
          <filter id="wbg"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <circle cx="40" cy="40" r="28" fill="none" stroke="url(#wb1)" strokeWidth="2" opacity="0.85" filter="url(#wbg)"/>
        <circle cx="40" cy="40" r="28" fill="#4A9EE8" opacity="0.05"/>
        <ellipse cx="40" cy="40" rx="13" ry="28" fill="none" stroke="#4A9EE8" strokeWidth="1.2" opacity="0.5"/>
        <line x1="12" y1="40" x2="68" y2="40" stroke="#4A9EE8" strokeWidth="1.2" opacity="0.5"/>
        <path d="M16 27 Q40 21 64 27" fill="none" stroke="#4A9EE8" strokeWidth="1" opacity="0.4"/>
        <path d="M16 53 Q40 59 64 53" fill="none" stroke="#4A9EE8" strokeWidth="1" opacity="0.4"/>
        <circle cx="40" cy="12" r="3.5" fill="#7DBFF5" filter="url(#wbg)" opacity="0.95"/>
        <circle cx="40" cy="68" r="3.5" fill="#7DBFF5" filter="url(#wbg)" opacity="0.95"/>
        <circle cx="68" cy="40" r="3" fill="#4A9EE8" opacity="0.8"/>
        <circle cx="12" cy="40" r="3" fill="#4A9EE8" opacity="0.8"/>
      </svg>
    ),
    location: (
      <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <defs>
          <linearGradient id="lc1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#7DBFF5"/><stop offset="100%" stopColor="#2478C8"/></linearGradient>
          <filter id="lcg"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        </defs>
        <path d="M40 10 Q56 10 56 28 Q56 44 40 66 Q24 44 24 28 Q24 10 40 10 Z" fill="none" stroke="url(#lc1)" strokeWidth="2" opacity="0.9" filter="url(#lcg)"/>
        <path d="M40 10 Q56 10 56 28 Q56 44 40 66 Q24 44 24 28 Q24 10 40 10 Z" fill="#4A9EE8" opacity="0.08"/>
        <circle cx="40" cy="28" r="8" fill="none" stroke="#7DBFF5" strokeWidth="1.8" opacity="0.8"/>
        <circle cx="40" cy="28" r="4" fill="#7DBFF5" opacity="0.95" filter="url(#lcg)"/>
        <ellipse cx="40" cy="70" rx="14" ry="4" fill="#4A9EE8" opacity="0.15"/>
        <circle cx="40" cy="10" r="3" fill="#7DBFF5" opacity="0.9"/>
        <circle cx="56" cy="28" r="2.5" fill="#4A9EE8" opacity="0.7"/>
        <circle cx="24" cy="28" r="2.5" fill="#4A9EE8" opacity="0.7"/>
      </svg>
    ),
  };
  return icons[type] || icons['phone'];
};

export default function CTA() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const contacts = [
    { href: 'tel:+971 4 888 6288', iconType: 'phone', label: t('cta.phone'), value: '+971 4 888 6288' },
    { href: 'mailto:info@alebelenergy.com', iconType: 'email', label: t('cta.email'), value: 'info@alebelenergy.com' },
    { href: 'https://www.alebelenergy.com', iconType: 'web', label: t('cta.website'), value: 'www.alebelenergy.com' },
  ];

  return (
    <section id="contact" className="py-16 md:py-32 relative overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: `radial-gradient(circle at 50% 50%, #4A9EE8 1px, transparent 1px)`, backgroundSize: '60px 60px' }} />
      </div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-gold/5 rounded-full blur-3xl" />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 relative">
        <div className="rounded-3xl overflow-hidden border border-gold/15 bg-white/5 backdrop-blur-sm">
          <div className="py-12 md:py-20 px-5 sm:px-8 lg:px-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">

              {/* Left */}
              <div className={`space-y-6 md:space-y-8 ${isRTL ? 'lg:order-2' : ''}`}>
                <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="w-12 h-px bg-gold" />
                  <span className="text-gold text-sm font-medium tracking-widest uppercase">{t('cta.label')}</span>
                </div>

                {/* FIXED: smaller font on mobile */}
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-white leading-tight">
                  {t('cta.title1')}
                  <span className="block text-gold" style={{ textShadow: '0 0 30px rgba(74,158,232,0.4)' }}>
                    {t('cta.title2')}
                  </span>
                </h2>

                <p className="text-base sm:text-xl text-white/60 leading-relaxed max-w-lg">{t('cta.description')}</p>

                {/* FIXED: full width buttons on mobile, row on sm+ */}
                <div className={`flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 pt-2 ${isRTL ? 'sm:flex-row-reverse' : ''}`}>
                  <Link to="/contact"
                    className={`px-6 sm:px-8 py-3 sm:py-4 bg-gold text-white font-semibold text-base sm:text-lg rounded-lg transition-all duration-300 flex items-center justify-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}
                    style={{ boxShadow: '0 0 24px rgba(74,158,232,0.25)' }}>
                    <i className="ri-mail-send-line text-xl" />
                    {t('cta.contactUs')}
                  </Link>
                  <a href="tel:042843117"
                    className={`px-6 sm:px-8 py-3 sm:py-4 border border-gold/30 text-gold font-semibold text-base sm:text-lg rounded-lg hover:bg-gold/10 transition-all duration-300 flex items-center justify-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <i className="ri-phone-line text-xl" />
                    {t('cta.callNow')}
                  </a>
                </div>
              </div>

              {/* Right — Contact cards */}
              <div className={`space-y-3 sm:space-y-4 ${isRTL ? 'lg:order-1' : ''}`}>
                {contacts.map((item) => (
                  <a key={item.label} href={item.href}
                    target={item.href.startsWith('http') ? '_blank' : undefined}
                    rel="noopener noreferrer"
                    className={`group flex items-center gap-4 bg-white/5 rounded-2xl p-4 sm:p-6 border border-gold/10 hover:border-gold/35 transition-all duration-300 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    {/* FIXED: smaller icon on mobile */}
                    <div className="w-10 h-10 sm:w-16 sm:h-16 flex-shrink-0">
                      <ContactIcon type={item.iconType} />
                    </div>
                    <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : 'text-left'}`}>
                      <div className="text-white/40 text-xs sm:text-sm mb-1 tracking-wider uppercase">{item.label}</div>
                      {/* FIXED: truncate long values, use dir=ltr for phone/email */}
                      <div className="text-white text-sm sm:text-lg font-medium truncate" dir="ltr">{item.value}</div>
                    </div>
                    <i className={`${isRTL ? 'ri-arrow-left-up-line' : 'ri-arrow-right-up-line'} text-gold text-lg sm:text-xl opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0`} />
                  </a>
                ))}

                {/* Location card — FIXED: QR smaller, text doesn't wrap badly */}
                <div className={`flex items-center gap-4 bg-white/5 rounded-2xl p-4 sm:p-6 border border-gold/10 ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <div className="w-10 h-10 sm:w-16 sm:h-16 flex-shrink-0">
                    <ContactIcon type="location" />
                  </div>
                  <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : 'text-left'}`}>
                    <div className="text-white/40 text-xs sm:text-sm mb-1 tracking-wider uppercase">{t('cta.location')}</div>
                    <div className="text-white text-sm sm:text-base font-medium leading-snug">
                      {t('contact.address.line1')}
                    </div>
                    <div className="text-white text-sm sm:text-base font-medium leading-snug">
                      {t('contact.address.line2')}
                    </div>
                    <div className="text-white/60 text-xs sm:text-sm mt-0.5">
                      {t('contact.address.line3')}
                    </div>
                  </div>
                  {/* FIXED: QR smaller on mobile, hidden on very small if needed */}
                  <div className="flex-shrink-0">
                    <img
                      src="/locationwhite.png"
                      alt="Location QR"
                      className="w-14 h-14 sm:w-20 sm:h-20 rounded-lg"
                      style={{ backgroundColor: 'white', padding: '3px', borderRadius: '8px' }}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                    />
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
